/**
 * /fleet/:vehicleId/edit — edición y borrado de un vehículo.
 *
 * Hasta ahora la ficha de /fleet/:id era de solo lectura: la API ya aceptaba
 * PATCH, pero no había ninguna pantalla que lo usara, así que un dato mal
 * tecleado en el alta (o importado del Excel) no se podía corregir desde la
 * aplicación.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ApiError, deleteVehicle, getVehicle, patchVehicle } from '@/lib/api'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { VehicleForm, type VehicleFormValues } from '@/components/vehicle-form'

export function FleetEditPage() {
  const { vehicleId } = useParams({ from: '/fleet/$vehicleId/edit' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState(false)

  const query = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => getVehicle(vehicleId),
  })

  const save = useMutation({
    mutationFn: (values: VehicleFormValues) => patchVehicle(vehicleId, values),
    onSuccess: async () => {
      // El vehículo aparece en tres sitios: su ficha, el listado y los
      // selectores de inspecciones y órdenes. Si no se invalidan, el usuario
      // guarda, vuelve atrás y sigue viendo el dato viejo.
      await qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
      void navigate({ to: '/fleet/$vehicleId', params: { vehicleId } })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message)
        setFields(err.fields ?? {})
      } else setError('No se pudo guardar.')
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteVehicle(vehicleId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vehicles'] })
      void navigate({ to: '/fleet' })
    },
    onError: (err) => {
      setConfirmDelete(false)
      // El 409 con código HAS_HISTORY no es un fallo: es la respuesta correcta
      // a intentar borrar un vehículo con inspecciones u órdenes. Su mensaje ya
      // explica que se retire cambiando el estado, así que se muestra tal cual.
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.')
    },
  })

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>
  if (query.isError || !query.data) {
    return <p className="text-sm text-danger">No se pudo cargar el vehículo.</p>
  }

  const v = query.data.vehicle

  return (
    <>
      <PageHeader
        title={`Editar ${v.unitNumber}`}
        actions={
          <Link
            to="/fleet/$vehicleId"
            params={{ vehicleId }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </Link>
        }
      />

      <Card className="mx-auto max-w-xl">
        <CardBody>
          <VehicleForm
            initial={v}
            submitLabel="Guardar cambios"
            pending={save.isPending}
            error={error}
            fieldErrors={fields}
            onSubmit={(values) => {
              setError(null)
              setFields({})
              save.mutate(values)
            }}
            extraActions={
              confirmDelete ? (
                <div className="flex gap-2 sm:flex-1">
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    loading={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    Confirmar borrado
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmDelete(false)}
                  >
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="sm:flex-1"
                  // Dos pasos a propósito: borrar arrastra en cascada odómetro,
                  // componentes, gastos y mantenimientos del vehículo.
                  onClick={() => setConfirmDelete(true)}
                >
                  Eliminar
                </Button>
              )
            }
          />
        </CardBody>
      </Card>
    </>
  )
}
